import { motion } from "framer-motion";
import { ArrowRight, Clock } from "lucide-react";
import { IMAGES } from "../../data/product";
import { useBag } from "../../context/BagContext";
import { trackAddToCart } from "../../lib/metaPixel";
import { JUDGEME_REVIEWS_VISIBLE } from "../../lib/judgeme";

const REVEAL = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};
const reveal = (delay = 0) => ({ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] });
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const CardShell = ({ image, imageAlt, comingSoon, testId, delay, children }) => (
  <motion.div
    {...REVEAL}
    transition={reveal(delay)}
    className="flex flex-col rounded-2xl overflow-hidden bg-[#FFFDF9] border border-[rgba(58,42,37,0.08)]"
    data-testid={testId}
  >
    <div className="relative bg-[#EDE3D6]">
      <img
        src={image}
        alt={imageAlt}
        loading="lazy"
        className={`w-full h-72 sm:h-80 object-contain p-8 ${comingSoon ? "opacity-90" : ""}`}
        data-testid={`${testId}-image`}
      />
      {comingSoon && (
        <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#3A2A25] text-[#EDE3D6] text-[10px] font-mono-accent uppercase tracking-[0.2em]" data-testid={`${testId}-badge`}>
          <Clock size={11} strokeWidth={2} />
          Coming Soon
        </span>
      )}
    </div>
    <div className="flex flex-col flex-1 p-6 sm:p-8">{children}</div>
  </motion.div>
);

const ConditionerCard = ({ product }) => {
  const { addToBag } = useBag();
  const price = product?.price ?? 29.99;
  const currency = product?.currency ?? "USD";
  const money = new Intl.NumberFormat("en-US", { style: "currency", currency }).format(price);

  return (
    <CardShell image={IMAGES.packshot} imageAlt="CELUSTA Leave-In Conditioner Spray 200 ml" testId="essentials-card-conditioner" delay={0.1}>
      <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#C9A45C]">Hair Care — Available Now</p>
      <h3 className="mt-2 font-serif-display font-medium text-2xl text-[#2B1F1B]" data-testid="essentials-card-conditioner-name">
        CELUSTA Leave-In Conditioner
      </h3>
      {JUDGEME_REVIEWS_VISIBLE && (
        <div className="mt-1.5 jdgm-widget jdgm-preview-badge" data-id="15964123070794" data-testid="essentials-judgeme-badge"></div>
      )}
      <p className="mt-3 text-sm font-light text-[#756A64] leading-relaxed">
        Lightweight leave-in spray that softens, smooths and enhances natural-looking shine — no rinsing required. 200 ml.
      </p>
      <div className="mt-auto pt-6 flex items-center justify-between gap-4">
        <p className="font-serif-display text-2xl text-[#3A2A25]" data-testid="essentials-card-conditioner-price">{money}</p>
        <button
          type="button"
          onClick={() => { addToBag(1); trackAddToCart(product, 1); }}
          className="px-7 py-3.5 rounded-full bg-[#3A2A25] text-white text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#55423A] hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgba(58,42,37,0.25)] transition-[background-color,transform,box-shadow] duration-300"
          data-testid="essentials-add-to-bag-button"
        >
          Add to Bag
        </button>
      </div>
      <button
        type="button"
        onClick={() => scrollTo("shop")}
        className="mt-5 inline-flex items-center gap-2 self-start text-xs font-mono-accent uppercase tracking-[0.18em] text-[#756A64] hover:text-[#3A2A25] transition-colors duration-300"
        data-testid="essentials-view-details-link"
      >
        View full details
        <ArrowRight size={13} strokeWidth={2} />
      </button>
    </CardShell>
  );
};

const BodySerumCard = () => (
  <CardShell image="/images/body-serum-placeholder.svg" imageAlt="CELUSTA Body Serum — coming soon" comingSoon testId="essentials-card-body-serum" delay={0.2}>
    <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#C9A45C]">Body Care — In Development</p>
    <h3 className="mt-2 font-serif-display font-medium text-2xl text-[#2B1F1B]" data-testid="essentials-card-body-serum-name">
      CELUSTA Body Serum
    </h3>
    <p className="mt-3 text-sm font-light text-[#756A64] leading-relaxed">
      A new everyday body-care essential is on the way — crafted with the same devotion to simple, elevated care.
    </p>
    <div className="mt-auto pt-6 flex items-center justify-between gap-4">
      <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-[#9A8D85]">Be first to know</p>
      <button
        type="button"
        onClick={() => scrollTo("body-care")}
        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-[rgba(58,42,37,0.25)] text-[#3A2A25] text-xs font-semibold uppercase tracking-[0.2em] hover:bg-[#EDE3D6] transition-colors duration-300"
        data-testid="essentials-body-serum-waitlist-button"
      >
        Be First to Know
        <ArrowRight size={13} strokeWidth={2} />
      </button>
    </div>
  </CardShell>
);

export const EverydayEssentials = ({ product }) => (
  <section id="essentials" className="bg-[#F8F4EC] scroll-mt-28" data-testid="essentials-section">
    <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
      <motion.div {...REVEAL} transition={reveal(0)} className="max-w-2xl">
        <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#C9A45C] mb-4">The Essentials</p>
        <h2 className="font-serif-display font-medium text-4xl sm:text-5xl tracking-tight text-[#2B1F1B] leading-tight" data-testid="essentials-heading">
          Your Everyday Essentials
        </h2>
        <p className="mt-4 text-sm sm:text-base font-light text-[#756A64] leading-relaxed">
          Thoughtful essentials for hair, body and everyday beauty — a growing line, designed around your routine.
        </p>
      </motion.div>
      <div className="mt-12 grid sm:grid-cols-2 gap-6 lg:gap-10">
        <ConditionerCard product={product} />
        <BodySerumCard />
      </div>
    </div>
  </section>
);
